import type { ReactNode } from 'react';
import { Bot } from 'lucide-react';
import { parseComposerReferences } from '@runloop/reflex-contract';
import { contextRefKindMeta } from '@reflex/ui/components/shared/ContextRefBadge';
import { MAX_HOVER_CHARS } from './turn-nav.js';

const CHIP_CLASS =
  'mx-0.5 inline-flex max-w-full items-center gap-1 rounded-md border border-border/60 bg-muted/50 py-0.5 pr-1 pl-1.5 align-middle text-xs text-foreground';

/** Static chip matching ContextRefBadge look, without its hover card. */
function ContextRefChip({ kind, title }: { kind: string; title: string }) {
  const { Icon } = contextRefKindMeta(kind);
  return (
    <span data-testid={`context-ref-badge-${kind}`} className={CHIP_CLASS}>
      <Icon className="size-3 shrink-0 text-muted-foreground" />
      <span className="truncate">{title}</span>
    </span>
  );
}

/** Static chip matching AgentRunBadge look, without its hover card. */
function AgentRunChip({ title }: { title: string }) {
  return (
    <span data-testid="agent-run-badge" className={CHIP_CLASS}>
      <Bot className="size-3 shrink-0 text-muted-foreground" />
      <span className="truncate">{title}</span>
    </span>
  );
}

/**
 * Prompt preview for the Turns rail tooltip: plain text plus static reference
 * chips (look like the chat badges, no nested hover). Caps length the same way
 * the plain hover string does.
 */
export function TurnHoverPreview({
  content,
  maxChars = MAX_HOVER_CHARS,
}: {
  content: string;
  maxChars?: number;
}) {
  const segments = parseComposerReferences(content.trim());
  const nodes: ReactNode[] = [];
  let used = 0;
  let truncated = false;

  for (let i = 0; i < segments.length; i++) {
    if (used >= maxChars) {
      truncated = true;
      break;
    }
    const seg = segments[i];
    if (seg.type === 'text') {
      let value = seg.value.replace(/\n{2,}/g, '\n');
      if (i === 0) value = value.replace(/^\s+/, '');
      if (i === segments.length - 1) value = value.replace(/\s+$/, '');
      const remaining = maxChars - used;
      if (value.length > remaining) {
        value = value.slice(0, remaining).trimEnd();
        truncated = true;
      }
      used += value.length;
      if (value) {
        nodes.push(
          <span key={i} className="whitespace-pre-line">
            {value}
          </span>,
        );
      }
      if (truncated) break;
      continue;
    }

    used += seg.title.length;
    nodes.push(
      seg.type === 'context' ? (
        <ContextRefChip key={i} kind={seg.kind} title={seg.title} />
      ) : (
        <AgentRunChip key={i} title={seg.title} />
      ),
    );
  }

  return (
    <span className="inline leading-relaxed">
      {nodes}
      {truncated ? '\u2026' : null}
    </span>
  );
}
